import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useImpersonation } from '@/hooks/useImpersonation';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import WysiwygEditor from '@/components/WysiwygEditor';
import { Package, ShoppingCart, Plus, Edit, Trash2, AlertCircle, DollarSign, LogOut, Eye, BarChart3, Users, Warehouse, Clock, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useCurrency } from '@/hooks/useCurrency';
import { useTranslations } from '@/hooks/useTranslations';
import { useLanguage } from '@/hooks/useLanguage';
import DOMPurify from 'dompurify';
import { formatPriceWithCurrency } from '@/utils/currency';
import { VendorMessagingCenter } from '@/components/vendor/MessagingCenter';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { VendorReviews } from '@/components/vendor/VendorReviews';
import { OrderFilters } from '@/components/vendor/OrderFilters';
import { VendorStats } from '@/components/vendor/VendorStats';
import { usePersistentTabState } from '@/hooks/usePersistentTabState';
import { ProductFilters } from '@/components/vendor/ProductFilters';
import { ProductImageUpload } from '@/components/products/ProductImageUpload';
import { VendorBalance } from '@/components/vendor/VendorBalance';
import { useCategories } from '@/hooks/useCategories';

// Helper function to sanitize HTML content
const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html);
};

interface Product {
  id: string;
  title: string;
  description: string | null;
  price_cents: number;
  currency: string;
  stock: number;
  category: string | null;
  brand: string | null;
  images: string[];
  active: boolean;
  created_at: string;
}

interface Relay {
  id: string;
  name: string;
  address: string;
  active: boolean;
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  product: {
    title: string;
    images: string[];
  };
  relay: {
    name: string;
    address: string;
  };
}

const VendorDashboard = () => {
  const { user, profile, signOut, isEffectiveVendor } = useAuth();
  const { getEffectiveUserId } = useImpersonation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { displayCurrency, convertPrice } = useCurrency();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { categories } = useCategories(true);
  const { unreadCount, markAsSeen } = useUnreadMessages();
  const [activeTab, setActiveTab] = usePersistentTabState(
    'vendor-dashboard:activeTab',
    'products',
    ['products', 'orders', 'balance', 'reviews', 'messages']
  );
  
  // Charger les vendor IDs (propriétaire + magasins gérés)
  const [vendorIds, setVendorIds] = useState<string[]>([]);
  const [accessLoading, setAccessLoading] = useState(true);
  
  useEffect(() => {
    const fetchVendorAccess = async () => {
      const effectiveUserId = getEffectiveUserId();
      if (!effectiveUserId) {
        setAccessLoading(false);
        return;
      }

      try {
        const ids: string[] = [effectiveUserId]; // Toujours inclure l'utilisateur impersonifié ou réel

        // Récupérer les magasins gérés
        const { data: managedStores } = await supabase
          .from('vendor_managers')
          .select('vendor_id')
          .eq('manager_id', effectiveUserId);

        if (managedStores && managedStores.length > 0) {
          managedStores.forEach(store => {
            if (!ids.includes(store.vendor_id)) {
              ids.push(store.vendor_id);
            }
          });
          console.log('[VendorDashboard] Managing stores:', managedStores);
        }

        setVendorIds(ids);
      } catch (error) {
        console.error('[VendorDashboard] Error fetching vendor access:', error);
        setVendorIds([effectiveUserId]); // Fallback: au moins l'utilisateur impersonifié ou réel
      } finally {
        setAccessLoading(false);
      }
    };

    fetchVendorAccess();
  }, [getEffectiveUserId]);
  
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [relays, setRelays] = useState<Relay[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [productForm, setProductForm] = useState({
    title: '',
    description: '',
    price_cents: 0,
    currency: 'CDF',
    stock: 0,
    category: '',
    brand: '',
    model: '',
    selectedRelays: [] as string[],
    images: [] as string[]
  });

  // Filtres pour les commandes
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderSearchQuery, setOrderSearchQuery] = useState('');
  const [orderDateFilter, setOrderDateFilter] = useState('all');
  const [orderSortBy, setOrderSortBy] = useState('created_at');
  const [orderSortOrder, setOrderSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filtres pour les produits
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [productSortBy, setProductSortBy] = useState('created_at');
  const [productSortOrder, setProductSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filtrer les commandes
  const filteredOrders = orders.filter(order => {
    // Filtre par statut
    if (orderStatusFilter !== 'all' && order.status !== orderStatusFilter) return false;

    // Filtre par recherche
    if (orderSearchQuery && !order.id.toLowerCase().includes(orderSearchQuery.toLowerCase())) {
      return false;
    }

    // Filtre par date
    if (orderDateFilter !== 'all') {
      const orderDate = new Date(order.created_at);
      const now = new Date();
      
      switch (orderDateFilter) {
        case 'today':
          if (orderDate.toDateString() !== now.toDateString()) return false;
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          if (orderDate < weekAgo) return false;
          break;
        case 'month':
          if (orderDate.getMonth() !== now.getMonth() || orderDate.getFullYear() !== now.getFullYear()) return false;
          break;
        case 'year':
          if (orderDate.getFullYear() !== now.getFullYear()) return false;
          break;
      }
    }

    return true;
  });

  // Filtrer et trier les produits
  const filteredProducts = products
    .filter(product => {
      if (productStatusFilter !== 'all') {
        if (productStatusFilter === 'active' && !product.active) return false;
        if (productStatusFilter === 'inactive' && product.active) return false;
      }
      if (productCategoryFilter !== 'all' && product.category !== productCategoryFilter) return false;
      if (productSearchQuery) {
        const query = productSearchQuery.toLowerCase();
        return (
          product.title.toLowerCase().includes(query) ||
          product.brand?.toLowerCase().includes(query)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (productSortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'price':
          comparison = a.price_cents - b.price_cents;
          break;
        case 'stock':
          comparison = a.stock - b.stock;
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        default:
          comparison = 0;
      }
      
      return productSortOrder === 'asc' ? comparison : -comparison;
    });

  const clearOrderFilters = () => {
    setOrderStatusFilter('all');
    setOrderSearchQuery('');
    setOrderDateFilter('all');
    setOrderSortBy('created_at');
    setOrderSortOrder('desc');
  };

  const clearProductFilters = () => {
    setProductStatusFilter('all');
    setProductSearchQuery('');
    setProductCategoryFilter('all');
    setProductSortBy('created_at');
    setProductSortOrder('desc');
  };

  const handleToggleOrderSortOrder = () => {
    setOrderSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleToggleProductSortOrder = () => {
    setProductSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  useEffect(() => {
    if (user && vendorIds.length > 0 && !accessLoading) {
      fetchProducts();
      fetchOrders();
      fetchRelays();
    }
  }, [user, vendorIds, accessLoading]);

  // Subscribe to real-time order updates for all managed vendors
  useEffect(() => {
    if (!user || vendorIds.length === 0) return;

    const channels = vendorIds.map(vendorId => {
      return supabase
        .channel(`vendor-orders-changes-${vendorId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'orders',
            filter: `vendor_id=eq.${vendorId}`
          },
          () => {
            fetchOrders();
          }
        )
        .subscribe();
    });

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
    };
  }, [vendorIds]);

  const fetchRelays = async () => {
    try {
      const { data, error } = await supabase
        .from('relays')
        .select('id, name, address, active')
        .eq('active', true)
        .order('name');

      if (error) throw error;
      setRelays(data || []);
    } catch (error) {
      console.error('Error fetching relays:', error);
    }
  };

  const fetchProducts = async () => {
    if (vendorIds.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .in('vendor_id', vendorIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchOrders = async () => {
    if (vendorIds.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          product:products(title, images),
          relay:relays(name, address)
        `)
        .in('vendor_id', vendorIds)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (productForm.selectedRelays.length === 0) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner au moins un point relais",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingProduct) {
        const { error } = await supabase
          .from('products')
          .update({
            title: productForm.title,
            description: productForm.description,
            price_cents: productForm.price_cents,
            currency: productForm.currency,
            stock: productForm.stock,
            category: productForm.category,
            brand: productForm.brand || null,
            model: productForm.model || null,
            images: productForm.images
          })
          .eq('id', editingProduct.id);

        if (error) throw error;

        // Update relay availability
        await supabase
          .from('product_relay_availability')
          .delete()
          .eq('product_id', editingProduct.id);

        const relayInserts = productForm.selectedRelays.map(relayId => ({
          product_id: editingProduct.id,
          relay_id: relayId
        }));

        const { error: relayError } = await supabase
          .from('product_relay_availability')
          .insert(relayInserts);

        if (relayError) throw relayError;

        toast({ title: "Produit mis à jour avec succès" });
      } else {
        const effectiveUserId = getEffectiveUserId();
        const { data: newProduct, error } = await supabase
          .from('products')
          .insert({
            vendor_id: effectiveUserId,
            title: productForm.title,
            description: productForm.description,
            price_cents: productForm.price_cents,
            currency: productForm.currency,
            stock: productForm.stock,
            category: productForm.category,
            brand: productForm.brand || null,
            model: productForm.model || null,
            images: productForm.images
          })
          .select()
          .single();

        if (error) throw error;

        const relayInserts = productForm.selectedRelays.map(relayId => ({
          product_id: newProduct.id,
          relay_id: relayId
        }));

        const { error: relayError } = await supabase
          .from('product_relay_availability')
          .insert(relayInserts);

        if (relayError) throw relayError;

        toast({ title: "Produit créé avec succès" });
      }

      fetchProducts();
      setShowProductDialog(false);
      resetProductForm();
    } catch (error: any) {
      toast({ 
        title: "Erreur", 
        description: error.message,
        variant: "destructive" 
      });
    }
  };

  const resetProductForm = () => {
    setProductForm({
      title: '',
      description: '',
      price_cents: 0,
      currency: 'CDF',
      stock: 0,
      category: '',
      brand: '',
      model: '',
      selectedRelays: [],
      images: []
    });
    setEditingProduct(null);
  };

  const handleEditProduct = async (product: Product) => {
    // Fetch relay availability for this product
    const { data: relayData, error } = await supabase
      .from('product_relay_availability')
      .select('relay_id')
      .eq('product_id', product.id);

    if (error) {
      console.error('Error fetching relay availability:', error);
    }

    setProductForm({
      title: product.title,
      description: product.description || '',
      price_cents: product.price_cents,
      currency: product.currency || 'CDF',
      stock: product.stock,
      category: product.category || '',
      model: (product as any).model || '',
      brand: product.brand || '',
      selectedRelays: relayData?.map(r => r.relay_id) || [],
      images: product.images
    });
    setEditingProduct(product);
    setShowProductDialog(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      toast({ title: "Produit supprimé avec succès" });
      fetchProducts();
    } catch (error: any) {
      toast({ 
        title: "Erreur", 
        description: error.message,
        variant: "destructive" 
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'PENDING_PAYMENT': { variant: 'secondary' as const },
      'PAID': { variant: 'default' as const },
      'AWAITING_DROP_OFF': { variant: 'destructive' as const },
      'AVAILABLE_FOR_PICKUP': { variant: 'default' as const },
      'PICKED_UP': { variant: 'secondary' as const },
      'EXPIRED': { variant: 'destructive' as const },
      'DISPUTE': { variant: 'destructive' as const },
      'CANCELLED': { variant: 'destructive' as const },
    };

    const fallbackByLang: Record<string, Record<string, string>> = {
      fr: { CANCELLED: 'Annulé' },
      en: { CANCELLED: 'Cancelled' },
      'lg-fr': { CANCELLED: 'Ebatalaki' },
      sw: { CANCELLED: 'Imeghairiwa' },
    };
    const fallback = fallbackByLang[language]?.[status] ?? fallbackByLang['fr'][status] ?? status;
    const label = t(`orders.status.${status}`, fallback);

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const };
    return <Badge variant={config.variant}>{label}</Badge>;
  };


  // Check if user can access vendor dashboard (admin or approved vendor)
  if (!isEffectiveVendor) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-16">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <CardTitle>Accès non autorisé</CardTitle>
              <CardDescription>
                Vous devez être un vendeur approuvé ou administrateur pour accéder à cette page.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate('/become-vendor')}>
                Devenir vendeur
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Show pending status for vendors with pending status
  if (profile?.vendor_status === 'pending') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-16">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <AlertCircle className="h-12 w-12 text-warning mx-auto mb-4" />
              <CardTitle>Validation en cours</CardTitle>
              <CardDescription>
                Votre demande de vendeur est en cours de validation par notre équipe.
                Vous recevrez un email dès que votre compte sera approuvé.
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Show rejected status for vendors with rejected status
  if (profile?.vendor_status === 'rejected') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container py-16">
          <Card className="max-w-md mx-auto">
            <CardHeader className="text-center">
              <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <CardTitle>Demande refusée</CardTitle>
              <CardDescription>
                Votre demande de vendeur a été refusée. 
                Contactez notre support pour plus d'informations.
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="container py-4 md:py-8 pb-24 md:pb-8">
        <div className="max-w-6xl">
          {/* Mobile Header - Compact */}
          <div className="mb-4 md:mb-8">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-xl md:text-3xl font-bold">Dashboard Vendeur</h1>
              <Button variant="ghost" size="sm" onClick={signOut} className="md:hidden">
                <LogOut className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={signOut} className="hidden md:flex">
                <LogOut className="h-4 w-4 mr-2" />
                Déconnexion
              </Button>
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              Gérez vos produits et commandes
            </p>
          </div>

          {/* Stats */}
          <VendorStats orders={orders} vendorIds={vendorIds} />

          {/* Main Content - Mobile Optimized Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              setActiveTab(v);
              if (v === 'messages') markAsSeen();
            }}
            className="w-full mt-4 md:mt-8"
          >
            {/* Mobile & Desktop: Grid layout */}
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 gap-1 h-auto p-1 bg-white shadow-sm">
              <TabsTrigger value="products" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <Package className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Produits</span>
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <ShoppingCart className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Commandes</span>
              </TabsTrigger>
              <TabsTrigger value="balance" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <DollarSign className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Finances</span>
              </TabsTrigger>
              <TabsTrigger value="reviews" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2">
                <BarChart3 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Avis</span>
              </TabsTrigger>
              <TabsTrigger value="messages" className="flex-col gap-0.5 h-auto py-2 px-1 text-[10px] md:text-sm md:flex-row md:gap-2 relative">
                <Users className="h-3.5 w-3.5 md:h-4 md:w-4" />
                <span>Messages</span>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center min-w-[16px] px-0.5">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Products Tab - Mobile Optimized */}
            <TabsContent value="products" className="space-y-3">
              <Card className="border-gray-200 shadow-sm">
                <CardHeader className="p-4 md:p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base md:text-lg">Mes produits</CardTitle>
                      <CardDescription className="text-xs md:text-sm mt-1">
                        Gérez votre catalogue
                      </CardDescription>
                    </div>
                    <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                      <DialogTrigger asChild>
                        <Button onClick={resetProductForm} size="sm" className="flex-shrink-0 text-xs md:text-sm h-8 md:h-9 px-2 md:px-3">
                          <Plus className="h-3.5 w-3.5 md:h-4 md:w-4 md:mr-2" />
                          <span className="hidden md:inline">Nouveau produit</span>
                          <span className="md:hidden ml-1">Ajouter</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-[95vw] md:max-w-md max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>
                            {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                          </DialogTitle>
                          <DialogDescription>
                            Remplissez les informations de votre produit
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleProductSubmit} className="space-y-4">
                          <div>
                            <Label htmlFor="title">Titre</Label>
                            <Input
                              id="title"
                              value={productForm.title}
                              onChange={(e) => setProductForm(prev => ({ ...prev, title: e.target.value }))}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="description">Description</Label>
                            <WysiwygEditor
                              value={productForm.description}
                              onChange={(value) => setProductForm(prev => ({ ...prev, description: value }))}
                              placeholder="Décrivez votre produit en détail..."
                              className="min-h-[200px]"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="brand">Marque</Label>
                              <Input
                                id="brand"
                                value={productForm.brand}
                                onChange={(e) => setProductForm(prev => ({ ...prev, brand: e.target.value }))}
                                placeholder="Ex: Samsung, Apple, Nike..."
                              />
                            </div>
                            <div>
                              <Label htmlFor="model">Modèle</Label>
                              <Input
                                id="model"
                                value={productForm.model}
                                onChange={(e) => setProductForm(prev => ({ ...prev, model: e.target.value }))}
                                placeholder="Ex: iPhone 15 Pro, Galaxy S24..."
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="price">Prix ({productForm.currency})</Label>
                              <Input
                                id="price"
                                type="number"
                                step="0.01"
                                value={productForm.price_cents / 100}
                                onChange={(e) => setProductForm(prev => ({ ...prev, price_cents: Math.round(parseFloat(e.target.value) * 100) }))}
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor="stock">Stock</Label>
                              <Input
                                id="stock"
                                type="number"
                                value={productForm.stock}
                                onChange={(e) => setProductForm(prev => ({ ...prev, stock: parseInt(e.target.value) }))}
                                required
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="currency">Devise</Label>
                            <Select
                              value={productForm.currency}
                              onValueChange={(value) => {
                                const oldCurrency = productForm.currency;
                                const currentPrice = productForm.price_cents;
                                
                                // Conversion rates (approximate)
                                const rates: Record<string, Record<string, number>> = {
                                  'CDF': { 'USD': 1/2800, 'EUR': 1/3000, 'CDF': 1 },
                                  'USD': { 'CDF': 2800, 'EUR': 0.93, 'USD': 1 },
                                  'EUR': { 'CDF': 3000, 'USD': 1.08, 'EUR': 1 }
                                };
                                
                                // Convert price if currency changes
                                let newPrice = currentPrice;
                                if (oldCurrency && oldCurrency !== value) {
                                  const conversionRate = rates[oldCurrency]?.[value] || 1;
                                  newPrice = Math.round(currentPrice * conversionRate);
                                }
                                
                                setProductForm(prev => ({ 
                                  ...prev, 
                                  currency: value,
                                  price_cents: newPrice
                                }));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner une devise" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CDF">Franc Congolais (CDF)</SelectItem>
                                <SelectItem value="USD">Dollar Américain (USD)</SelectItem>
                                <SelectItem value="EUR">Euro (EUR)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="category">Catégorie</Label>
                            <Select
                              value={productForm.category}
                              onValueChange={(value) => setProductForm(prev => ({ ...prev, category: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Sélectionner une catégorie" />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map((category) => (
                                  <SelectItem key={category.id} value={category.slug}>
                                    {category.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <ProductImageUpload
                            images={productForm.images}
                            onImagesChange={(images) => setProductForm(prev => ({ ...prev, images }))}
                            maxImages={5}
                          />
                          <div>
                            <Label>Points relais disponibles</Label>
                            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3 mt-2">
                              {relays.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Aucun point relais disponible</p>
                              ) : (
                                relays.map((relay) => (
                                  <div key={relay.id} className="flex items-start space-x-2">
                                    <input
                                      type="checkbox"
                                      id={`relay-${relay.id}`}
                                      checked={productForm.selectedRelays.includes(relay.id)}
                                      onChange={(e) => {
                                        const relayId = relay.id;
                                        setProductForm(prev => ({
                                          ...prev,
                                          selectedRelays: e.target.checked
                                            ? [...prev.selectedRelays, relayId]
                                            : prev.selectedRelays.filter(id => id !== relayId)
                                        }));
                                      }}
                                      className="mt-1"
                                    />
                                    <label htmlFor={`relay-${relay.id}`} className="text-sm cursor-pointer flex-1">
                                      <div className="font-medium">{relay.name}</div>
                                      <div className="text-muted-foreground text-xs">{relay.address}</div>
                                    </label>
                                  </div>
                                ))
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                              Sélectionnez les points relais où ce produit sera disponible
                            </p>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowProductDialog(false)}>
                              Annuler
                            </Button>
                            <Button type="submit">
                              {editingProduct ? 'Modifier' : 'Créer'}
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <ProductFilters
                    statusFilter={productStatusFilter}
                    searchQuery={productSearchQuery}
                    categoryFilter={productCategoryFilter}
                    sortBy={productSortBy}
                    sortOrder={productSortOrder}
                    onStatusChange={setProductStatusFilter}
                    onSearchChange={setProductSearchQuery}
                    onCategoryChange={setProductCategoryFilter}
                    onSortByChange={setProductSortBy}
                    onSortOrderToggle={handleToggleProductSortOrder}
                    onClearFilters={clearProductFilters}
                  />
                  
                  {filteredProducts.length === 0 ? (
                     <div className="text-center py-12">
                       <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                       <p className="text-sm text-muted-foreground">
                         {products.length === 0 ? 'Aucun produit créé' : 'Aucun produit ne correspond aux filtres'}
                       </p>
                     </div>
                   ) : (
                     <div className="space-y-3">
                       {filteredProducts.map((product) => (
                        <Card key={product.id} className="border-gray-200 shadow-sm">
                          {/* Mobile: Vertical layout, Desktop: Horizontal */}
                          <div className="p-3 md:p-4">
                            <div className="flex gap-3">
                              {/* Product Image */}
                              {product.images?.[0] && (
                                <img
                                  src={product.images[0]}
                                  alt={product.title}
                                  className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg flex-shrink-0"
                                />
                              )}
                              
                              {/* Product Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <h3 className="font-semibold text-sm md:text-base line-clamp-2">{product.title}</h3>
                                  <p className="font-semibold text-sm md:text-base flex-shrink-0">
                                    {formatPriceWithCurrency(product.price_cents, product.currency, displayCurrency, convertPrice)}
                                  </p>
                                </div>
                                
                                {/* Description - Hidden on mobile */}
                                <div 
                                  className="hidden md:block text-sm text-muted-foreground line-clamp-2 mb-2"
                                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description || '') }}
                                />
                                
                                {/* Badges and Stock */}
                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                  <Badge variant="secondary" className="text-xs">
                                    {product.category ? t(`category.${product.category}`) : ''}
                                  </Badge>
                                  <Badge variant={product.active ? 'default' : 'secondary'} className="text-xs">
                                    {product.active ? 'Actif' : 'Inactif'}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    Stock: {product.stock}
                                  </span>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditProduct(product)}
                                    className="flex-1 md:flex-none"
                                  >
                                    <Edit className="h-3 w-3 mr-1" />
                                    <span className="text-xs">Modifier</span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteProduct(product.id)}
                                    className="flex-1 md:flex-none text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    <span className="text-xs">Supprimer</span>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Orders Tab - Mobile Optimized */}
            <TabsContent value="orders" className="space-y-3">
              <Card className="border-gray-200 shadow-sm">
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="text-base md:text-lg">Mes commandes</CardTitle>
                  <CardDescription className="text-xs md:text-sm">
                    Suivez les commandes de vos produits
                  </CardDescription>
                </CardHeader>
                 <CardContent className="space-y-4 p-4 md:p-6">
                   <OrderFilters
                     statusFilter={orderStatusFilter}
                     searchQuery={orderSearchQuery}
                     dateFilter={orderDateFilter}
                     sortBy={orderSortBy}
                     sortOrder={orderSortOrder}
                     onStatusChange={setOrderStatusFilter}
                     onSearchChange={setOrderSearchQuery}
                     onDateChange={setOrderDateFilter}
                     onSortByChange={setOrderSortBy}
                     onSortOrderToggle={handleToggleOrderSortOrder}
                     onClearFilters={clearOrderFilters}
                   />
                  
                  {loading ? (
                    <div className="text-center py-12">
                      <RefreshCw className="h-8 w-8 text-muted-foreground mx-auto mb-3 animate-spin" />
                      <p className="text-sm text-muted-foreground">Chargement...</p>
                    </div>
                  ) : filteredOrders.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-sm text-muted-foreground">
                        {orders.length === 0 ? 'Aucune commande' : 'Aucune commande ne correspond aux filtres'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredOrders.map((order) => (
                        <Card 
                          key={order.id} 
                          className="border-gray-200 shadow-sm cursor-pointer active:scale-[0.98] transition-all"
                          onClick={() => navigate(`/vendor-account/orders/${order.id}`)}
                        >
                          <div className="p-3 md:p-4">
                            <div className="flex gap-3">
                              {/* Order Image */}
                              {order.product.images?.[0] && (
                                <img
                                  src={order.product.images[0]}
                                  alt={order.product.title}
                                  className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg flex-shrink-0"
                                />
                              )}
                              
                              {/* Order Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-sm md:text-base line-clamp-2 mb-1">
                                      {order.product.title}
                                    </h3>
                                    <p className="text-xs text-muted-foreground mb-1">
                                      #{order.order_number}
                                    </p>
                                  </div>
                                  {getStatusBadge(order.status)}
                                </div>
                                
                                {/* Relay and Date - Mobile Compact */}
                                <div className="space-y-1 mb-3">
                                  <p className="text-xs text-muted-foreground flex items-center min-w-0">
                                    <Warehouse className="h-3 w-3 mr-1 flex-shrink-0" />
                                    <span className="truncate min-w-0 flex-1">{order.relay.name}</span>
                                  </p>
                                  <p className="text-xs text-muted-foreground flex items-center">
                                    <Clock className="h-3 w-3 mr-1 flex-shrink-0" />
                                    <span className="whitespace-nowrap">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>
                                  </p>
                                </div>
                                
                                {/* Price and Action */}
                                <div className="flex items-center justify-between">
                                  <p className="font-semibold text-sm md:text-base text-orange-600">
                                    {formatPriceWithCurrency(order.amount_cents || 0, order.currency || 'CDF', displayCurrency, convertPrice)}
                                  </p>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/vendor-account/orders/${order.id}`);
                                    }}
                                    className="text-xs"
                                  >
                                    <Eye className="h-3 w-3 md:mr-1" />
                                    <span className="hidden md:inline">Détails</span>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Balance Tab */}
            <TabsContent value="balance">
              <VendorBalance />
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages">
              <Card>
                <CardContent className="pt-6">
                  <VendorMessagingCenter />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <Card>
                <CardContent className="pt-6">
                  <VendorReviews />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default VendorDashboard;